import { useState, useMemo } from 'react';
import { useTasks, ASSIGN_ALL } from './TasksContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIORITIES  = ['Low', 'Medium', 'High', 'Urgent'];
const TASK_STATUS = ['To Do', 'In Progress', 'Done'];
const STAFF_ROLES = ['Admin', 'SuperAdmin', 'PM'];

const PRIORITY_CONFIG = {
  Low:    { color: 'var(--text-dim)',    bg: 'var(--bg-card)' },
  Medium: { color: 'var(--warning-text)', bg: 'var(--warning-bg)' },
  High:   { color: 'var(--accent)', bg: 'var(--danger-bg)' },
  Urgent: { color: '#ff3860', bg: '#3d0010' },
};

const STATUS_CONFIG = {
  'To Do':       { color: 'var(--text-dim)',    bg: 'var(--bg-card)' },
  'In Progress': { color: 'var(--warning-text)', bg: 'var(--warning-bg)' },
  'Done':        { color: 'var(--success-text)', bg: 'var(--success-bg)' },
};

const SORT_OPTIONS = [
  { key: 'deadline',  label: 'Deadline' },
  { key: 'priority',  label: 'Priority' },
  { key: 'status',    label: 'Status'   },
  { key: 'createdAt', label: 'Created'  },
];

const PRIORITY_ORDER = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
const STATUS_ORDER   = { 'To Do': 0, 'In Progress': 1, 'Done': 2 };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function PriorityBadge({ priority }) {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.Medium;
  return (
    <span style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}`, borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
      {priority}
    </span>
  );
}

/** Real <select> dropdown for status — так можна вибрати будь-який статус напряму. */
function StatusSelect({ status, onChange }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['To Do'];
  return (
    <select
      value={status}
      onChange={e => onChange(e.target.value)}
      style={{
        color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}`,
        borderRadius: '4px', padding: '2px 6px', fontSize: '11px', fontWeight: 'bold',
        cursor: 'pointer', outline: 'none',
      }}>
      {TASK_STATUS.map(s => <option key={s} value={s} style={{ background: 'var(--bg-card)', color: 'var(--text-body)' }}>{s}</option>)}
    </select>
  );
}

const inputStyle = {
  background: 'var(--bg-menu)', border: '1px solid var(--border-strong)', borderRadius: '6px',
  color: 'var(--text-body)', padding: '8px 12px', fontSize: '13px', outline: 'none',
  width: '100%', boxSizing: 'border-box',
};

// ─── Create / Edit Panel ──────────────────────────────────────────────────────

const blankForm = (assignedTo = ASSIGN_ALL) => ({
  title: '', description: '', assignedTo,
  status: 'To Do', projectId: null, projectName: '', deadline: '', priority: 'Medium',
});

// initialAssignedTo — дозволяє відкрити панель з уже обраним виконавцем
// (наприклад, кнопка "Task" у картці конкретного фрілансера).
export function TaskPanel({ task, onClose, onSave, projects = [], allUsers = [], initialAssignedTo }) {
  const [form, setForm] = useState(task ? {
    title:       task.title,
    description: task.description || '',
    assignedTo:  task.assignedTo,
    status:      task.status,
    projectId:   task.projectId   || null,
    projectName: task.projectName || '',
    deadline:    task.deadline    || '',
    priority:    task.priority,
  } : blankForm(initialAssignedTo || ASSIGN_ALL));

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleProjectChange = id => {
    const proj = projects.find(p => String(p.id) === String(id));
    set('projectId',   id ? Number(id) : null);
    set('projectName', proj ? proj.name : '');
  };

  const handleSave = () => {
    if (!form.title.trim()) return;
    onSave(form);
    onClose();
  };

  const Label = ({ children }) => (
    <p style={{ color: 'var(--text-dim)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 6px 0' }}>{children}</p>
  );

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '460px',
        background: 'var(--bg-panel)', borderLeft: '1px solid var(--border)',
        zIndex: 500, display: 'flex', flexDirection: 'column',
        fontFamily: 'Arial, sans-serif', boxShadow: '-8px 0 32px rgba(0,0,0,0.6)',
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '16px' }}>{task ? 'Edit Task' : 'New Task'}</h3>
          <span onClick={onClose} style={{ color: 'var(--text-dim)', fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}>✕</span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          <div>
            <Label>Title *</Label>
            <input style={inputStyle} placeholder="Task title..." value={form.title} onChange={e => set('title', e.target.value)} autoFocus />
          </div>

          <div>
            <Label>Description</Label>
            <textarea
              style={{ ...inputStyle, resize: 'none', height: '80px', fontFamily: 'Arial, sans-serif' }}
              placeholder="Details (optional)..."
              value={form.description}
              onChange={e => set('description', e.target.value)}
            />
          </div>

          <div>
            <Label>Assign To</Label>
            <select style={inputStyle} value={form.assignedTo || ''} onChange={e => set('assignedTo', e.target.value)}>
              <option value={ASSIGN_ALL}>🌐 All Users (everyone)</option>
              <option disabled>──────────</option>
              {allUsers.map(u => (
                <option key={u.id} value={u.id}>{u.name} — {u.role}</option>
              ))}
            </select>
            {form.assignedTo === ASSIGN_ALL && (
              <p style={{ color: 'var(--warning-text)', fontSize: '11px', margin: '6px 0 0 0' }}>
                ⚠️ This task will appear in every user's dashboard
              </p>
            )}
            {form.assignedTo && form.assignedTo !== ASSIGN_ALL && (
              <p style={{ color: 'var(--text-dim)', fontSize: '11px', margin: '6px 0 0 0' }}>
                ✉️ The assignee will get an email notification
              </p>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <Label>Status</Label>
              <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
                {TASK_STATUS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>Priority</Label>
              <select style={inputStyle} value={form.priority} onChange={e => set('priority', e.target.value)}>
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label>Deadline</Label>
            <input type="date" style={inputStyle} value={form.deadline} onChange={e => set('deadline', e.target.value)} />
          </div>

          <div>
            <Label>Project (optional)</Label>
            <select style={inputStyle} value={form.projectId || ''} onChange={e => handleProjectChange(e.target.value)}>
              <option value="">No Project</option>
              {projects.filter(p => !p.archived).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px' }}>
          <button onClick={handleSave} style={{ flex: 1, background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
            {task ? 'Save Changes' : 'Create Task'}
          </button>
          <button onClick={onClose} style={{ flex: 1, background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '10px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function TasksPage({ onBack, projects = [], allUsers = [], currentUser = null }) {
  const { tasks, addTask, updateTask, deleteTask, getMyTasks } = useTasks();

  const isStaff = STAFF_ROLES.includes(currentUser?.role);
  const visibleTasks = isStaff ? tasks : getMyTasks();

  const userName = id => allUsers.find(u => u.id === id)?.name || '—';

  const [statusFilter,   setStatusFilter]   = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [search,         setSearch]         = useState('');
  const [sortKey,        setSortKey]        = useState('deadline');
  const [sortDir,        setSortDir]        = useState('asc');
  const [panelOpen,      setPanelOpen]      = useState(false);
  const [editingTask,    setEditingTask]    = useState(null);

  const openNew    = ()     => { setEditingTask(null);  setPanelOpen(true); };
  const openEdit   = task   => { setEditingTask(task);  setPanelOpen(true); };
  const closePanel = ()     => { setPanelOpen(false);   setEditingTask(null); };

  const handleSave = form => {
    if (editingTask) updateTask(editingTask.id, form);
    else             addTask(form);
  };

  const handleStatusChange = (task, status) => updateTask(task.id, { status });

  const handleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let list = visibleTasks;
    if (statusFilter !== 'All') list = list.filter(t => t.status    === statusFilter);
    if (priorityFilter)         list = list.filter(t => t.priority   === priorityFilter);
    if (assigneeFilter)         list = list.filter(t => t.assignedTo === assigneeFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        t.title.toLowerCase().includes(q) ||
        (t.projectName || '').toLowerCase().includes(q) ||
        (t.assignedTo === ASSIGN_ALL ? 'all users' : userName(t.assignedTo)).toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      let av, bv;
      if      (sortKey === 'priority') { av = PRIORITY_ORDER[a.priority] ?? 9; bv = PRIORITY_ORDER[b.priority] ?? 9; }
      else if (sortKey === 'status')   { av = STATUS_ORDER[a.status]     ?? 9; bv = STATUS_ORDER[b.status]     ?? 9; }
      else                             { av = a[sortKey] || ''; bv = b[sortKey] || ''; }
      if (av < bv) return sortDir === 'asc' ? -1 :  1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTasks, statusFilter, priorityFilter, assigneeFilter, search, sortKey, sortDir]);

  const statusCounts = useMemo(() => ({
    All:           visibleTasks.length,
    'To Do':       visibleTasks.filter(t => t.status === 'To Do').length,
    'In Progress': visibleTasks.filter(t => t.status === 'In Progress').length,
    Done:          visibleTasks.filter(t => t.status === 'Done').length,
  }), [visibleTasks]);

  const today   = new Date().toISOString().slice(0, 10);
  const inputB  = { background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: '6px', color: 'var(--text-body)', padding: '7px 12px', fontSize: '13px', outline: 'none' };
  const thB     = { padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-card)', userSelect: 'none' };
  const tdB     = { padding: '12px 14px', color: 'var(--text-body)', fontSize: '13px', borderBottom: '1px solid var(--bg-hover)', verticalAlign: 'middle' };

  const SortIcon = ({ k }) => k === sortKey
    ? <span style={{ color: 'var(--accent)', marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
    : <span style={{ color: 'var(--border-strong)',    marginLeft: '4px' }}>↕</span>;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>

      {panelOpen && <TaskPanel task={editingTask} onClose={closePanel} onSave={handleSave} projects={projects} allUsers={allUsers} />}

      {/* Nav */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span onClick={onBack} style={{ color: 'var(--text-dim)', fontSize: '13px', cursor: 'pointer' }}>← Dashboard</span>
          <span style={{ color: 'var(--border-strong)' }}>|</span>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '16px', letterSpacing: '1px' }}>Tasks</span>
          <span style={{ background: 'var(--border)', color: 'var(--text-muted)', borderRadius: '10px', padding: '1px 8px', fontSize: '11px' }}>
            {filtered.length}
          </span>
        </div>
        {isStaff && (
          <button onClick={openNew} style={{ background: 'var(--accent)', border: 'none', color: 'var(--on-accent)', padding: '8px 20px', borderRadius: '6px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
            + New Task
          </button>
        )}
      </div>

      <div style={{ padding: '32px' }}>

        {/* Status tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'var(--bg-card)', borderRadius: '8px', padding: '4px', border: '1px solid var(--border)', width: 'fit-content' }}>
          {['All', 'To Do', 'In Progress', 'Done'].map(s => {
            const active = statusFilter === s;
            const cfg    = STATUS_CONFIG[s];
            return (
              <button key={s} onClick={() => setStatusFilter(s)} style={{
                background: active ? 'var(--border)' : 'transparent', border: 'none',
                color: active ? (cfg?.color || 'var(--text-primary)') : 'var(--text-dim)',
                padding: '6px 16px', borderRadius: '6px', fontSize: '13px',
                cursor: 'pointer', fontWeight: active ? 'bold' : 'normal',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                {s}
                <span style={{ background: active ? (cfg?.bg || 'var(--border-strong)') : 'var(--bg-subtle)', color: active ? (cfg?.color || 'var(--text-muted)') : 'var(--text-disabled)', borderRadius: '10px', padding: '0 6px', fontSize: '11px' }}>
                  {statusCounts[s] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input style={{ ...inputB, width: '220px' }} placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)} />
          <select style={inputB} value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
            <option value="">All Priorities</option>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
          {isStaff && (
            <select style={inputB} value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
              <option value="">All Assignees</option>
              <option value={ASSIGN_ALL}>🌐 All Users</option>
              {allUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          {(search || priorityFilter || assigneeFilter) && (
            <span onClick={() => { setSearch(''); setPriorityFilter(''); setAssigneeFilter(''); }} style={{ color: 'var(--accent)', fontSize: '12px', cursor: 'pointer' }}>
              Clear filters
            </span>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-dim)', fontSize: '12px' }}>Sort:</span>
            {SORT_OPTIONS.map(s => (
              <button key={s.key} onClick={() => handleSort(s.key)} style={{
                background: sortKey === s.key ? 'var(--border)' : 'transparent',
                border: '1px solid var(--border)', color: sortKey === s.key ? 'var(--text-body)' : 'var(--text-dim)',
                padding: '5px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
              }}>
                {s.label}<SortIcon k={s.key} />
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-disabled)', padding: '60px', fontSize: '14px', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            No tasks found
          </div>
        ) : (
          <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thB, color: 'var(--text-dim)' }}>Task</th>
                  <th style={{ ...thB, color: 'var(--text-dim)' }}>Project</th>
                  {isStaff && <th style={{ ...thB, color: 'var(--text-dim)' }}>Assigned To</th>}
                  <th style={{ ...thB, color: 'var(--text-dim)' }}>Created By</th>
                  <th style={{ ...thB, color: sortKey === 'priority' ? 'var(--text-body)' : 'var(--text-dim)', cursor: 'pointer' }} onClick={() => handleSort('priority')}>
                    Priority<SortIcon k="priority" />
                  </th>
                  <th style={{ ...thB, color: sortKey === 'deadline' ? 'var(--text-body)' : 'var(--text-dim)', cursor: 'pointer' }} onClick={() => handleSort('deadline')}>
                    Deadline<SortIcon k="deadline" />
                  </th>
                  <th style={{ ...thB, color: sortKey === 'status' ? 'var(--text-body)' : 'var(--text-dim)', cursor: 'pointer' }} onClick={() => handleSort('status')}>
                    Status<SortIcon k="status" />
                  </th>
                  <th style={{ ...thB, color: 'var(--text-dim)' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(task => {
                  const isOverdue = task.deadline && task.deadline < today && task.status !== 'Done';
                  return (
                    <tr key={task.id}
                      style={{ opacity: task.status === 'Done' ? 0.5 : 1 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                      <td style={tdB}>
                        <div style={{ color: task.status === 'Done' ? 'var(--text-dim)' : 'var(--text-primary)', fontWeight: 'bold', textDecoration: task.status === 'Done' ? 'line-through' : 'none' }}>
                          {task.title}
                        </div>
                        {task.description && (
                          <div style={{ color: 'var(--text-dim)', fontSize: '12px', marginTop: '2px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {task.description}
                          </div>
                        )}
                      </td>

                      <td style={tdB}>
                        {task.projectName
                          ? <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{task.projectName}</span>
                          : <span style={{ color: 'var(--border-strong)' }}>—</span>}
                      </td>

                      {isStaff && (
                        <td style={tdB}>
                          {task.assignedTo === ASSIGN_ALL
                            ? <span style={{ color: 'var(--info-text)', fontSize: '12px', fontWeight: 'bold' }}>🌐 All Users</span>
                            : <span style={{ color: 'var(--text-body)', fontSize: '12px' }}>{task.assignedToName || userName(task.assignedTo)}</span>}
                        </td>
                      )}

                      <td style={tdB}><span style={{ color: 'var(--text-faint)', fontSize: '12px' }}>{task.createdByName || '—'}</span></td>

                      <td style={tdB}><PriorityBadge priority={task.priority} /></td>

                      <td style={tdB}>
                        <span style={{ color: isOverdue ? 'var(--accent)' : 'var(--text-muted)', fontSize: '12px', fontWeight: isOverdue ? 'bold' : 'normal', whiteSpace: 'nowrap' }}>
                          {task.deadline ? (isOverdue ? `⚠️ ${task.deadline}` : task.deadline) : '—'}
                        </span>
                      </td>

                      <td style={tdB}>
                        <StatusSelect status={task.status} onChange={s => handleStatusChange(task, s)} />
                      </td>

                      <td style={{ ...tdB, whiteSpace: 'nowrap' }}>
                        {isStaff && (
                          <>
                            <button onClick={() => openEdit(task)} style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', marginRight: '6px' }}>
                              Edit
                            </button>
                            <button
                              onClick={() => deleteTask(task.id)}
                              style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-disabled)', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                              onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.borderColor = 'var(--accent)'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-disabled)';    e.currentTarget.style.borderColor = 'var(--border)'; }}>
                              ✕
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer summary */}
        {filtered.length > 0 && (
          <div style={{ display: 'flex', gap: '20px', marginTop: '12px', padding: '0 4px' }}>
            {['To Do', 'In Progress', 'Done'].map(s => (
              <span key={s} style={{ color: 'var(--text-dim)', fontSize: '12px' }}>
                {s}: <span style={{ color: STATUS_CONFIG[s].color }}>{statusCounts[s]}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TasksPage;
